import React, { useEffect, useState, useCallback } from 'react';
import DealerLayout from '../components/DealerLayout';
import api from '../api/axios';
import { C } from '../theme';

const STATUS_STYLE = {
  'dealer-stock': { bg: '#F5F3FF', color: '#6D28D9', label: 'Dealer Stock', dot: '#7C3AED' },
  'mapped':       { bg: '#ECFDF5', color: '#065F46', label: 'Mapped',       dot: '#059669' },
  'installed':    { bg: '#ECFDF5', color: '#065F46', label: 'Mapped',       dot: '#059669' },
  'in-stock':     { bg: '#EFF6FF', color: '#1D4ED8', label: 'In Stock',     dot: '#3B82F6' },
};
const getStatus = (item) => (item.is_mapped || item.status === 'installed') ? 'mapped' : item.status;

export default function DealerInventory() {
  const [items,    setItems]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [catFilter,  setCatFilter]  = useState('');
  const [stFilter,   setStFilter]   = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/api/dealer/inventory'); setItems(r.data); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const cats = [...new Set(items.map(it => it.category_name).filter(Boolean))];

  const filtered = items.filter(it => {
    const q = search.toLowerCase();
    const st = getStatus(it);
    return (
      (!q || it.serial_number?.toLowerCase().includes(q) || it.ref_number?.toLowerCase().includes(q)
        || it.item_id?.toLowerCase().includes(q) || it.type_name?.toLowerCase().includes(q)
        || it.mapped_client?.toLowerCase().includes(q))
      && (!catFilter || it.category_name === catFilter)
      && (!stFilter  || st === stFilter)
    );
  });

  const total    = items.length;
  const inStock  = items.filter(it => !it.is_mapped).length;
  const mapped   = items.filter(it => it.is_mapped).length;
  const byCat    = cats.reduce((acc, c) => { acc[c] = items.filter(it => it.category_name === c).length; return acc; }, {});

  const fmtDate = d => d || '—';

  return (
    <DealerLayout title="My Inventory">

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {[
          { label: 'Total Items',   val: total,   color: C.primary,  bg: C.primaryLight,  border: `${C.primary}40` },
          { label: 'Unallocated',   val: inStock,  color: '#1D4ED8',  bg: '#EFF6FF',       border: '#BFDBFE' },
          { label: 'Mapped',        val: mapped,   color: '#059669',  bg: '#ECFDF5',       border: '#6EE7B7' },
          ...cats.map(c => ({ label: c, val: byCat[c] || 0, color: '#6D28D9', bg: '#F5F3FF', border: '#DDD6FE' })),
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: '12px', padding: '14px 20px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div>
              <div style={{ fontSize: '22px', fontWeight: 800, color: s.color }}>{s.val}</div>
              <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Table card */}
      <div style={{ background: C.white, borderRadius: '12px', padding: '20px', border: `1px solid ${C.border}` }}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search serial, ref, item ID, type, client…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <select value={catFilter} onChange={e => setCatFilter(e.target.value)} style={selSt}>
            <option value="">All Categories</option>
            {cats.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selSt}>
            <option value="">All Status</option>
            <option value="dealer-stock">Dealer Stock</option>
            <option value="mapped">Mapped</option>
          </select>
          {(search || catFilter || stFilter) && (
            <button onClick={() => { setSearch(''); setCatFilter(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {total}</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Item ID', 'Category', 'Type', 'Serial Number', 'Ref Number', 'Status', 'Mapped To', 'Date'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={8} style={emptyCell}>
                  {items.length === 0 ? 'No inventory items assigned yet.' : 'No results match the filters.'}
                </td></tr>
              ) : filtered.map((it, i) => {
                const st = getStatus(it);
                const stStyle = STATUS_STYLE[st] || STATUS_STYLE['dealer-stock'];
                return (
                  <tr key={it.id} style={{ background: i % 2 === 0 ? C.white : '#FDFAFF', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>{it.item_id || `#${it.id}`}</span>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '3px 10px', borderRadius: '20px', fontSize: '11px', fontWeight: 700, background: '#F5F3FF', color: '#6D28D9', border: '1px solid #DDD6FE' }}>
                        {it.category_name || '—'}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '13px', color: C.textMain, fontWeight: 500 }}>{it.type_name || '—'}</span>
                    </td>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: C.textMain }}>{it.serial_number || '—'}</span>
                    </td>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', color: C.textMain }}>{it.ref_number || '—'}</span>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: stStyle.bg, color: stStyle.color }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: stStyle.dot, display: 'inline-block', marginRight: '5px', verticalAlign: 'middle' }} />
                        {stStyle.label}
                      </span>
                    </td>
                    <td style={td}>
                      {it.is_mapped ? (
                        <div>
                          <div style={{ fontSize: '13px', fontWeight: 600, color: '#065F46' }}>{it.mapped_client}</div>
                          <div style={{ fontSize: '11px', color: C.textSub, fontFamily: 'monospace' }}>{it.mapping_id}</div>
                        </div>
                      ) : (
                        <span style={{ color: C.textSub, fontSize: '12px' }}>—</span>
                      )}
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '12px', color: C.textSub }}>{fmtDate(it.created_at)}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </DealerLayout>
  );
}

const th       = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td       = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selSt    = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnGhost = { padding: '9px 16px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
